import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHero, Section, Eyebrow } from "@/components/PageParts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Search, Building2 } from "lucide-react";

const SERVICES = ["Assisted Living", "Family Care Homes", "Memory Care", "Independent Living",
  "Private-Duty Home Care", "Personal Care", "Companion Care", "Residential Respite", "In-Home Respite",
  "Adult Day Services", "Care Management", "Senior Transition / Move Management", "Private Transportation"];

export default function Providers() {
  const nav = useNavigate();
  const [capacity, setCapacity] = useState([]);
  const [service, setService] = useState("all");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    const params = {};
    if (service !== "all") params.service = service;
    if (state) params.state = state;
    if (city) params.city = city;
    if (q) params.q = q;
    api.get("/capacity", { params }).then((r) => setCapacity(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [service]);

  return (
    <div>
      <PageHero eyebrow="Provider network" title="Available private-pay care capacity"
        subtitle="Search live availability across many services and geographies. Rose Care Homes and Eagle Healthcare may participate — alongside hundreds of independent providers." />
      <Section className="py-16">
        <Card className="p-6" data-testid="capacity-search">
          <div className="grid gap-3 md:grid-cols-5">
            <Select value={service} onValueChange={setService}>
              <SelectTrigger data-testid="cap-service"><SelectValue placeholder="Service" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="cap-state" placeholder="State (e.g. NC)" value={state} onChange={(e) => setState(e.target.value)} />
            <Input data-testid="cap-city" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
            <Input data-testid="cap-q" placeholder="Keyword" value={q} onChange={(e) => setQ(e.target.value)} />
            <Button data-testid="cap-search-btn" onClick={load} className="bg-navy hover:bg-navy/90">
              <Search className="mr-2 h-4 w-4" /> Search
            </Button>
          </div>
        </Card>

        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {capacity.map((c) => (
            <Card key={c.id} className="p-6" data-testid={`capacity-card-${c.id}`}>
              <div className="flex items-center justify-between">
                <Badge className="bg-sky text-clinical hover:bg-sky">{c.service_category}</Badge>
                {c.provider_type && <span className="text-xs text-slate-400">{c.provider_type}</span>}
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{c.description}</h3>
              <div className="mt-3 flex items-center gap-1.5 text-sm text-slate-500">
                <Building2 className="h-4 w-4" /> {c.provider_name}
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 text-sm text-slate-500">
                <MapPin className="h-4 w-4" /> {c.city}, {c.state}
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4 text-sm">
                <span className="text-slate-400">{c.quantity}</span>
                <span className="font-semibold text-emeraldbrand">{c.price_range}</span>
              </div>
            </Card>
          ))}
          {capacity.length === 0 && (
            <p className="col-span-full py-10 text-center text-slate-400">No capacity matches your search.</p>
          )}
        </div>

        <Card className="mt-12 flex flex-col items-center gap-4 bg-slate-50 p-10 text-center">
          <h3 className="font-heading text-2xl font-semibold text-navy">Become a participating provider</h3>
          <p className="max-w-xl text-slate-500">List your services, geography, pricing, and available capacity. Compensation agreements are never automatically approved.</p>
          <Button data-testid="providers-apply-btn" onClick={() => nav("/provider-application")} className="bg-navy hover:bg-navy/90">
            Provider onboarding form
          </Button>
        </Card>
      </Section>
    </div>
  );
}
